import { LeaderboardType, type ClubLeaderboardEntry, type LeaderboardEntry } from '@rw/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { Page } from '../../components/Layout';
import { EmptyState, QueryView } from '../../components/states';
import { Avatar, Button } from '../../components/ui';
import { useClubLeaderboard, useLeaderboard, useRegion } from '../../hooks/queries';
import { ClubBadge } from '../clubs/ClubBadge';
import { formatKm, formatNumber } from '../../lib/format';
import { useAuth } from '../../stores/auth';
import { MY_COLOR } from '../../lib/colors';
import { useT } from '../../i18n';

const TABS: Array<{ type: LeaderboardType; label: string; unit: string }> = [
  { type: LeaderboardType.XP, label: 'XP', unit: 'XP' },
  { type: LeaderboardType.DISTANCE, label: 'Distance', unit: 'km' },
  { type: LeaderboardType.TERRITORY, label: 'Territory', unit: 'sectors' },
];

export function LeaderboardPage() {
  const [tab, setTab] = useState<LeaderboardType>(LeaderboardType.XP);
  const [scope, setScope] = useState<'runners' | 'clubs'>('runners');
  // Which board: one city, or the whole country ('all'; not for territory: sectors only mean something in their city).
  const { data: regions, region: current } = useRegion();
  const manyCities = (regions?.length ?? 0) > 1;
  const [place, setPlace] = useState<string | null>(null);
  const city = place && place !== 'all' ? place : current?.id;
  const regionId = tab === 'territory' ? city : manyCities && place !== 'all' ? city : undefined;
  const q = useLeaderboard(tab, regionId);
  const me = useAuth((s) => s.user?.id);
  const tr = useT();
  const unit = tr(TABS.find((t) => t.type === tab)!.unit);
  const fmt = (v: number) => (tab === 'distance' ? formatKm(v, 1) : formatNumber(v));

  return (
    <Page>
      <div className="flex items-end justify-between">
        <h1 className="font-display text-4xl font-extrabold uppercase">{tr('Rankings')}</h1>
        <div className="flex rounded-full bg-ink-850 p-1 text-xs font-bold uppercase">
          {(['runners', 'clubs'] as const).map((sc) => (
            <button
              key={sc}
              onClick={() => setScope(sc)}
              data-testid={`lb-scope-${sc}`}
              className={`rounded-full px-3 py-1.5 ${scope === sc ? 'bg-ink-600 text-ink-100' : 'text-ink-400'}`}
            >
              {tr(sc === 'runners' ? 'Runners' : 'Clubs')}
            </button>
          ))}
        </div>
      </div>
      <Link to="/events" className="mt-3 flex items-center justify-between rounded-2xl border border-gold/30 bg-gold/10 px-4 py-2.5 text-sm" data-testid="events-link">
        <span className="font-semibold text-gold">{tr('🏁 Events & Hall of Fame')}</span>
        <span className="text-ink-400">›</span>
      </Link>
      <div className="mt-4 grid grid-cols-3 gap-1 rounded-2xl bg-ink-850 p-1" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.type}
            role="tab"
            aria-selected={tab === t.type}
            data-testid={`lb-tab-${t.type}`}
            onClick={() => setTab(t.type)}
            className={`h-11 rounded-xl font-display text-base font-bold uppercase tracking-wide transition ${
              tab === t.type ? 'bg-volt text-ink-950' : 'text-ink-300'
            }`}
          >
            {tr(t.label)}
          </button>
        ))}
      </div>

      {manyCities ? (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="lb-places">
          {tab !== 'territory' ? (
            <PlaceChip label={tr('Nationwide')} on={!regionId} onClick={() => setPlace('all')} testId="lb-place-all" />
          ) : null}
          {regions!.map((r) => (
            <PlaceChip key={r.id} label={r.name} on={regionId === r.id} onClick={() => setPlace(r.id)} testId={`lb-place-${r.id}`} />
          ))}
        </div>
      ) : null}

      {scope === 'clubs' ? (
        <ClubBoard type={tab} unit={unit} fmt={fmt} regionId={regionId} />
      ) : (
      <QueryView
        query={{ ...q, data: q.data }}
        empty={(d) =>
          d.pages[0]!.total === 0 ? (
            <EmptyState icon="🏆" title={tr('No rankings yet')} body={tr('Only validated runs count. Finish a run to claim the top spot.')} />
          ) : null
        }
      >
        {(data) => {
          const mine = data.pages[0]!.me;
          const entries = data.pages.flatMap((p) => p.entries);
          // Pin my row only when it isn't already visible in the list.
          const pinned = mine && !entries.some((e) => e.user.id === mine.user.id);
          return (
            <>
              {pinned ? (
                <div className="mt-4">
                  <div className="mb-1 text-[11px] font-semibold uppercase tracking-widest text-ink-400">{tr('Your rank')}</div>
                  <div className="rounded-2xl border border-volt/40 bg-volt/10" data-testid="lb-me">
                    <Row e={mine} isMe value={fmt(mine.value)} unit={unit} />
                  </div>
                </div>
              ) : !mine ? (
                <p className="mt-4 text-center text-sm text-ink-400">{tr('Finish a validated run to get ranked.')}</p>
              ) : null}
              <ol className="mt-3 divide-y divide-ink-800 overflow-hidden rounded-2xl border border-ink-700/80 bg-ink-850" data-testid="lb-list">
                {entries.map((e) => (
                  <li key={e.user.id} className={e.user.id === me ? 'bg-volt/10' : ''} data-testid={e.user.id === me ? 'lb-me-row' : undefined}>
                    <Row e={e} isMe={e.user.id === me} value={fmt(e.value)} unit={unit} />
                  </li>
                ))}
              </ol>
              {q.hasNextPage ? (
                <Button variant="secondary" className="mt-3 w-full" loading={q.isFetchingNextPage} onClick={() => q.fetchNextPage()}>
                  {tr('Load more')}
                </Button>
              ) : null}
            </>
          );
        }}
      </QueryView>
      )}
    </Page>
  );
}

function PlaceChip({ label, on, onClick, testId }: { label: string; on: boolean; onClick: () => void; testId: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-full border px-3 py-1 text-xs font-bold ${on ? 'border-volt bg-volt text-ink-950' : 'border-ink-700 text-ink-300'}`}
    >
      {label}
    </button>
  );
}

function ClubBoard({ type, unit, fmt, regionId }: { type: LeaderboardType; unit: string; fmt: (v: number) => string; regionId?: string }) {
  const q = useClubLeaderboard(type, regionId);
  const t = useT();
  return (
    <QueryView
      query={q}
      empty={(d) => (d.entries.length === 0 ? <EmptyState icon="⬢" title={t('No clubs yet')} body={t('Create a club and conquer the city as a crew.')} /> : null)}
    >
      {(d) => (
        <>
          {d.mine && !d.entries.some((e) => e.club.id === d.mine!.club.id) ? (
            <div className="mt-4">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-widest text-ink-400">{t('Your club')}</div>
              <div className="rounded-2xl border border-volt/40 bg-volt/10" data-testid="club-lb-mine">
                <ClubRowEntry e={d.mine} value={fmt(d.mine.value)} unit={unit} mine />
              </div>
            </div>
          ) : null}
          <ol className="mt-3 divide-y divide-ink-800 overflow-hidden rounded-2xl border border-ink-700/80 bg-ink-850" data-testid="club-lb-list">
            {d.entries.map((e) => (
              <li key={e.club.id} className={d.mine?.club.id === e.club.id ? 'bg-volt/10' : ''}>
                <ClubRowEntry e={e} value={fmt(e.value)} unit={unit} mine={d.mine?.club.id === e.club.id} />
              </li>
            ))}
          </ol>
        </>
      )}
    </QueryView>
  );
}

function ClubRowEntry({ e, value, unit, mine }: { e: ClubLeaderboardEntry; value: string; unit: string; mine: boolean }) {
  const t = useT();
  return (
    <Link to={`/clubs/${e.club.id}`} className="flex items-center gap-3 px-4 py-3">
      <span className="w-8 text-center font-display text-xl font-extrabold tabular text-ink-400">{e.rank}</span>
      <ClubBadge club={e.club} size="sm" link={false} />
      <span className="min-w-0 flex-1">
        <span className={`block truncate font-semibold ${mine ? 'text-volt' : ''}`}>{e.club.name}</span>
        <span className="text-xs text-ink-500">
          {t(e.members === 1 ? '{n} member' : '{n} members', { n: e.members })}
        </span>
      </span>
      <span className="text-right">
        <span className="font-display text-xl font-bold tabular">{value}</span>
        <span className="ml-1 text-xs text-ink-400">{unit}</span>
      </span>
    </Link>
  );
}

function Row({ e, isMe, value, unit }: { e: LeaderboardEntry; isMe: boolean; value: string; unit: string }) {
  const t = useT();
  const medal = e.rank === 1 ? 'text-gold' : e.rank === 2 ? 'text-ink-200' : e.rank === 3 ? 'text-[#d08b4c]' : 'text-ink-500';
  return (
    <Link to={isMe ? '/profile' : `/players/${e.user.id}`} className="flex items-center gap-3 px-4 py-3">
      <span className={`w-8 text-center font-display text-xl font-extrabold tabular ${medal}`}>{e.rank}</span>
      <Avatar name={e.user.displayName} color={isMe ? MY_COLOR : e.user.color} size="sm" src={e.user.avatarUrl} />
      <span className="min-w-0 flex-1">
        <span className={`block truncate font-semibold ${isMe ? 'text-volt' : ''}`}>{isMe ? t('{name} (you)', { name: e.user.displayName }) : e.user.displayName}</span>
        <span className="text-xs text-ink-500">{t('Level {n}', { n: e.level })}</span>
      </span>
      <span className="text-right">
        <span className="font-display text-xl font-bold tabular">{value}</span>
        <span className="ml-1 text-xs text-ink-400">{unit}</span>
      </span>
    </Link>
  );
}
